import type { DeviceState, WebsiteFeed, WebsiteService } from "@vaulted/shared";
import { useQuery } from "@tanstack/react-query";
import QRCode from "qrcode";
import { type CSSProperties, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { get } from "../api";

/**
 * The lobby's animated menu (docs/lobby-signage.md): the screen saver on a lobby display, cycling
 * through the clinic's services, prices, memberships, and specials, live from the website feed. Laid
 * out on a 1080 × 1920 stage scaled to the screen. Any picture not uploaded yet is an animated
 * gradient instead. A tap anywhere wakes the welcome.
 */

type Signage = NonNullable<NonNullable<DeviceState["lobbyMedia"]>["signage"]>;
type Media = NonNullable<DeviceState["lobbyMedia"]>;
interface Scene {
  key: string;
  ms: number;
  render: () => ReactNode;
  /** What a tap does here, said at the bottom ("Tap to book The Fountain"); none: check in or book. */
  tapLabel?: string;
}

const W = 1080;
const H = 1920;
/** Featured services shown per loop (the list rotates loop to loop). */
const FEATURED_PER_LOOP = 3;
const FADE_MS = 900;

/** "$165" (or "$45.50" when there are cents). */
const price = (cents: number) => `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
/** A plan's benefit line without the fine print in brackets ("4 × The Base per month"). */
const benefitText = (b: string) => b.replace(/\s*\([^)]*\)/g, "").replace(/,\s*,/g, ",").replace(/,\s*$/, "").trim();

export function LobbySignage({ slug, clinicName, media, banner, onWake }: { slug: string; clinicName: string; media: Media; banner: ReactNode; onWake: (path?: string) => void }) {
  const signage = media.signage!;
  // The menu, refreshed every 5 minutes; a failed refresh keeps showing the last one.
  const feed = useQuery({ queryKey: ["signage-feed", slug], queryFn: () => get<WebsiteFeed>(`/v1/public/clinics/${slug}/website`), refetchInterval: 5 * 60_000, retry: 2, staleTime: 60_000 });
  const scale = useStageScale();
  const [loop, setLoop] = useState(0);
  const scenes = useMemo(() => (feed.data ? buildScenes(feed.data, signage, media, loop, slug) : []), [feed.data, signage, media, loop]);
  const [at, setAt] = useState(0);
  const [leaving, setLeaving] = useState<Scene | null>(null);
  useNightlyReload();
  usePreload(Object.values(signage.art).filter((u): u is string => !!u));

  // Next scene when this one's time is up (a new loop rotates the featured services).
  const current = scenes.length ? scenes[at % scenes.length]! : null;
  useEffect(() => {
    if (!current) return;
    const t = setTimeout(() => {
      setLeaving(current);
      if (at + 1 >= scenes.length) (setLoop((l) => l + 1), setAt(0));
      else setAt(at + 1);
    }, current.ms);
    return () => clearTimeout(t);
  }, [current?.key, at, scenes.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!leaving) return;
    const t = setTimeout(() => setLeaving(null), FADE_MS);
    return () => clearTimeout(t);
  }, [leaving]);

  return (
    <button
      type="button"
      // A tap on something that opens a page (a featured service, a membership, a special) goes there; anywhere else wakes the welcome.
      onClick={(e) => onWake((e.target as HTMLElement).closest("[data-tap]")?.getAttribute("data-tap") ?? undefined)}
      className="fixed inset-0 z-50 cursor-default overflow-hidden bg-black text-white"
      aria-label="Tap to start"
    >
      <style>{CSS}</style>
      <div className="absolute left-1/2 top-1/2 overflow-hidden" style={{ width: W, height: H, transform: `translate(-50%, -50%) scale(${scale})` }}>
        {current ? (
          <>
            {leaving && leaving.key !== current.key && (
              <div key={`out-${leaving.key}`} className="vv-out absolute inset-0">
                {leaving.render()}
              </div>
            )}
            <div key={`in-${current.key}-${loop}`} className="vv-in absolute inset-0">
              {current.render()}
            </div>
          </>
        ) : (
          <Gradient />
        )}
        {/* Always on top: the next opening, and how to start. */}
        <div className="absolute inset-x-0 top-[70px] flex justify-center px-10">{banner}</div>
        <div className="absolute inset-x-0 bottom-[60px] flex items-center justify-center gap-6">
          {signage.art["logo-mark"] && <img src={signage.art["logo-mark"]} alt="" className="h-[44px] opacity-80" />}
          <span className="rounded-full bg-black/45 px-8 py-3 text-[30px] backdrop-blur">{current?.tapLabel ?? "Tap anywhere to check in or book"}</span>
        </div>
        {!current && <div className="absolute inset-0 flex items-center justify-center font-display text-[90px]">{clinicName}</div>}
      </div>
    </button>
  );
}

// ── Scenes

function buildScenes(feed: WebsiteFeed, s: Signage, media: Media, loop: number, slug: string): Scene[] {
  const art = (slot: string | null | undefined) => (slot ? (s.art[slot] ?? null) : null);
  const shown = feed.services.filter((x) => x.inClinic && !s.hidden.includes(x.id));
  const byId = new Map(feed.services.map((x) => [x.id, x]));
  const out: Scene[] = [];

  out.push({ key: "intro", ms: 6500, render: () => <Intro feed={feed} bg={art("brand-bg")} light={art("brand-light")} logo={art("logo")} /> });
  if (media.videoUrl) out.push({ key: "video", ms: 12_000, render: () => <video src={media.videoUrl!} autoPlay muted playsInline className="absolute inset-0 size-full object-cover" /> });

  // Featured: a few per loop, taking turns.
  const featured = s.featured.map((f) => ({ f, svc: byId.get(f.serviceId) })).filter((x): x is { f: Signage["featured"][number]; svc: WebsiteService } => !!x.svc && x.svc.inClinic);
  const n = Math.min(FEATURED_PER_LOOP, featured.length);
  for (let i = 0; i < n; i++) {
    const { f, svc } = featured[(loop * FEATURED_PER_LOOP + i) % featured.length]!;
    out.push({
      key: `featured-${svc.id}`,
      ms: 7500,
      tapLabel: `Tap to book ${svc.name}`,
      render: () => (
        <div className="absolute inset-0" data-tap={`/book/${slug}?service=${svc.id}`}>
          <Featured svc={svc} bg={svc.photoUrl ?? art(f.art)} droplets={art("droplets")} />
        </div>
      ),
    });
  }

  // A menu board per category (in the menu's order).
  for (const c of feed.categories) {
    // Low to high price, like a menu (ties alphabetical).
    const rows = shown.filter((x) => x.category === c.name).sort((a, b) => a.priceCents - b.priceCents || a.name.localeCompare(b.name));
    if (!rows.length) continue;
    out.push({ key: `board-${c.name}`, ms: Math.min(13_000, 4500 + rows.length * 650), render: () => <Board title={c.name} subtitle={c.description} rows={rows} bg={art("menu-bg")} side={art(s.boardArt[c.name])} /> });
  }

  const memberships = feed.plans.filter((p) => p.kind === "membership");
  const packages = feed.plans.filter((p) => p.kind === "package");
  if (memberships.length || packages.length)
    out.push({ key: "plans", ms: 10_000, tapLabel: "Tap a membership or package to join", render: () => <Plans memberships={memberships} packages={packages} bg={art("vault-bg")} slug={slug} /> });

  for (const x of media.specials)
    out.push({
      key: `special-${x.id}`,
      ms: 7500,
      tapLabel: x.path ? "Tap to learn more" : undefined,
      render: () => (
        <div className="absolute inset-0" data-tap={x.path ?? undefined}>
          <Special special={x} bg={art("special-bg")} />
        </div>
      ),
    });

  out.push({ key: "book", ms: 8000, render: () => <Book feed={feed} bg={art("lounge-bg")} logo={art("logo")} /> });
  return out;
}

/** A full-bleed picture with a slow push-in (or the animated gradient when there's none). */
function Backdrop({ src, ms = 9000, dim = 0.35 }: { src: string | null; ms?: number; dim?: number }) {
  return (
    <>
      {src ? <img src={src} alt="" className="vv-kenburns absolute inset-0 size-full object-cover" style={{ animationDuration: `${ms}ms` }} /> : <Gradient />}
      <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(0,0,0,${dim + 0.25}) 0%, rgba(0,0,0,${dim}) 30%, rgba(0,0,0,${dim}) 55%, rgba(0,0,0,0.85) 100%)` }} />
    </>
  );
}

function Gradient() {
  return <div className="vv-gradient absolute inset-0" />;
}

const rise = (delayMs: number): CSSProperties => ({ animationDelay: `${delayMs}ms` });

function Intro({ feed, bg, light, logo }: { feed: WebsiteFeed; bg: string | null; light: string | null; logo: string | null }) {
  return (
    <div className="absolute inset-0">
      <Backdrop src={bg} ms={8000} dim={0.1} />
      <div className="absolute inset-x-0 top-[700px] flex flex-col items-center gap-10 px-16 text-center">
        {logo ? <img src={logo} alt={feed.clinic.name} className="vv-reveal w-[820px]" /> : <span className="vv-reveal font-display text-[120px] leading-none">{feed.clinic.name}</span>}
        {feed.clinic.tagline && (
          <span className="vv-rise font-display text-[64px] italic text-white/90" style={rise(1300)}>
            {feed.clinic.tagline}
          </span>
        )}
      </div>
      {light ? <img src={light} alt="" className="vv-sweep absolute left-0 top-[880px] w-[1400px] mix-blend-screen" /> : <div className="vv-sweep absolute left-0 top-[930px] h-[6px] w-[700px] bg-gradient-to-r from-transparent via-white to-transparent opacity-80" />}
    </div>
  );
}

function Featured({ svc, bg, droplets }: { svc: WebsiteService; bg: string | null; droplets: string | null }) {
  return (
    <div className="absolute inset-0">
      <Backdrop src={bg} ms={8500} dim={0.2} />
      {/* Droplets stay in the top half, faded, so they never cross the words. */}
      {droplets && <img src={droplets} alt="" className="vv-float absolute inset-0 size-full object-cover opacity-50" style={{ maskImage: "linear-gradient(180deg, #000 0%, #000 30%, transparent 55%)", WebkitMaskImage: "linear-gradient(180deg, #000 0%, #000 30%, transparent 55%)" }} />}
      <div className="absolute inset-x-0 bottom-0 h-[1100px]" style={{ background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,.55) 35%, rgba(0,0,0,.85) 100%)" }} />
      <div className="vv-shadow absolute inset-x-0 bottom-[220px] flex flex-col gap-6 px-[90px]">
        <span className="vv-rise text-[34px] font-semibold uppercase tracking-[0.3em] text-[#ff4fa0]" style={rise(200)}>
          {svc.category}
        </span>
        <span className="vv-reveal font-display text-[120px] leading-[1.02]" style={rise(400)}>
          {svc.name}
        </span>
        {svc.description && (
          <span className="vv-rise line-clamp-2 text-[42px] leading-snug text-white/85" style={rise(900)}>
            {svc.description}
          </span>
        )}
        {svc.ingredients.length > 0 && (
          <div className="flex flex-wrap gap-3">
            {svc.ingredients.slice(0, 6).map((x, i) => (
              <span key={x} className="vv-rise rounded-full border border-white/30 bg-white/10 px-5 py-2 text-[28px] backdrop-blur" style={rise(1200 + i * 120)}>
                {x}
              </span>
            ))}
          </div>
        )}
        <span className="vv-rise vv-glow font-display text-[96px] text-white" style={rise(1600)}>
          {price(svc.priceCents)}
        </span>
      </div>
    </div>
  );
}

function Board({ title, subtitle, rows, bg, side }: { title: string; subtitle: string; rows: WebsiteService[]; bg: string | null; side: string | null }) {
  // Rows share the space between the heading and the bottom line, so a long menu still fits.
  const rowH = Math.min(96, Math.floor(1120 / Math.max(rows.length, 1)));
  const size = Math.min(58, Math.round(rowH * 0.52));
  return (
    <div className="absolute inset-0">
      <Backdrop src={bg} ms={14_000} dim={0.45} />
      {/* The board's picture sits beside the heading, above the list (never over the prices). */}
      {side && <img src={side} alt="" className="vv-sway absolute right-[20px] top-[150px] h-[400px] opacity-90" style={{ maskImage: "linear-gradient(180deg, #000 70%, transparent 100%)", WebkitMaskImage: "linear-gradient(180deg, #000 70%, transparent 100%)" }} />}
      <div className={`vv-shadow absolute inset-x-0 top-[250px] flex flex-col px-[90px] ${side ? "items-start text-left" : ""}`}>
        <span className="vv-reveal font-display text-[110px] leading-none">{title}</span>
        {subtitle && (
          <span className={`vv-rise mt-4 max-w-[640px] text-[36px] text-white/75 ${side ? "" : "mx-auto"}`} style={rise(400)}>
            {subtitle}
          </span>
        )}
        <div className={`relative flex w-full flex-col ${side ? "mt-[110px]" : "mt-12"}`}>
          <div className="vv-sweep-down pointer-events-none absolute inset-x-0 h-[120px] bg-gradient-to-b from-transparent via-[#c51a6a]/25 to-transparent" />
          {rows.map((r, i) => (
            <div key={r.id} className="vv-rise flex items-center gap-6 border-b border-white/15 py-2 text-left" style={{ ...rise(600 + i * 110), fontSize: size, minHeight: rowH }}>
              <span className="font-semibold leading-tight">{r.name}</span>
              <span className="flex-1" />
              <span className="font-display text-[#ff7ab8]">{price(r.priceCents)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Plans({ memberships, packages, bg, slug }: { memberships: WebsiteFeed["plans"]; packages: WebsiteFeed["plans"]; bg: string | null; slug: string }) {
  return (
    <div className="absolute inset-0">
      <Backdrop src={bg} ms={11_000} dim={0.45} />
      <div className="absolute inset-x-0 top-[260px] flex flex-col gap-10 px-[80px]">
        <span className="vv-reveal font-display text-[110px] leading-none">Memberships</span>
        <div className="flex flex-col gap-8">
          {memberships.slice(0, 3).map((p, i) => (
            <div key={p.id} data-tap={`/join/${slug}/${p.id}`} className="vv-flip rounded-[28px] border border-white/20 bg-black/55 px-12 py-10 backdrop-blur-md" style={rise(500 + i * 350)}>
              <div className="flex items-baseline gap-6">
                <span className="font-display text-[64px]">{p.name}</span>
                <span className="flex-1" />
                <span className="font-display text-[64px] text-[#ff7ab8]">
                  {price(p.priceCents)}
                  <span className="text-[34px] text-white/70">/mo</span>
                </span>
              </div>
              {p.benefits.slice(0, 3).map((b) => (
                <div key={b} className="mt-3 line-clamp-2 text-left text-[34px] text-white/85">
                  • {benefitText(b)}
                </div>
              ))}
            </div>
          ))}
        </div>
        {packages.length > 0 && (
          <div className="vv-rise flex flex-col gap-3" style={rise(1600)}>
            <span className="text-[32px] font-semibold uppercase tracking-[0.3em] text-[#ff4fa0]">Packages</span>
            {packages.slice(0, 4).map((p) => (
              <div key={p.id} data-tap={`/join/${slug}/${p.id}`} className="flex items-baseline gap-6 text-[40px]">
                <span>{p.name}</span>
                <span className="flex-1" />
                <span className="font-display">{price(p.priceCents)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Special({ special, bg }: { special: Media["specials"][number]; bg: string | null }) {
  return (
    <div className="absolute inset-0">
      <Backdrop src={bg} ms={8500} dim={0.3} />
      <div className="absolute inset-x-0 top-[650px] flex flex-col items-center gap-8 px-[90px] text-center">
        {special.ribbon && (
          <span className="vv-slide rounded-full bg-[#c51a6a] px-10 py-3 text-[40px] font-semibold" style={rise(200)}>
            {special.ribbon}
          </span>
        )}
        <span className="vv-pop font-display text-[110px] leading-[1.05]" style={rise(500)}>
          {special.headline}
        </span>
        {special.text && (
          <span className="vv-rise text-[44px] text-white/85" style={rise(1000)}>
            {special.text}
          </span>
        )}
      </div>
    </div>
  );
}

function Book({ feed, bg, logo }: { feed: WebsiteFeed; bg: string | null; logo: string | null }) {
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    void QRCode.toDataURL(feed.clinic.bookingUrl, { margin: 1, width: 360, color: { dark: "#000000", light: "#ffffff" } }).then(setQr).catch(() => setQr(null));
  }, [feed.clinic.bookingUrl]);
  return (
    <div className="absolute inset-0">
      <Backdrop src={bg} ms={9000} dim={0.4} />
      <div className="absolute inset-x-0 top-[330px] flex flex-col items-center gap-12 px-16 text-center">
        {logo ? <img src={logo} alt={feed.clinic.name} className="vv-reveal w-[620px]" /> : <span className="vv-reveal font-display text-[96px]">{feed.clinic.name}</span>}
        <span className="vv-pop vv-pulse font-display text-[130px] leading-none" style={rise(500)}>
          Tap to book
        </span>
        <span className="vv-rise text-[42px] text-white/85" style={rise(900)}>
          Or scan to book from your phone
        </span>
        {qr && <img src={qr} alt="" className="vv-rise w-[320px] rounded-[20px] border-[10px] border-white" style={rise(1200)} />}
        {feed.clinic.phone && (
          <span className="vv-rise text-[40px] text-white/80" style={rise(1500)}>
            {feed.clinic.phone}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Running unattended

/** The 1080 × 1920 stage, scaled to fit the screen. */
function useStageScale() {
  const calc = () => Math.min(window.innerWidth / W, window.innerHeight / H);
  const [scale, setScale] = useState(calc);
  useEffect(() => {
    const on = () => setScale(calc());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return scale;
}

/** A fresh start once a night (3 AM) so a screen left on for weeks never slows down. */
function useNightlyReload() {
  const started = useRef(Date.now());
  useEffect(() => {
    const t = setInterval(() => {
      if (new Date().getHours() === 3 && Date.now() - started.current > 3600_000) window.location.reload();
    }, 60_000);
    return () => clearInterval(t);
  }, []);
}

/** Load every picture up front so no scene starts blank. */
function usePreload(urls: string[]) {
  const key = urls.join("|");
  useEffect(() => {
    const imgs = urls.map((u) => Object.assign(new Image(), { src: u }));
    return () => void imgs.splice(0);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
}

const CSS = `
.vv-in { animation: vv-fade-in ${FADE_MS}ms ease-out both; }
.vv-out { animation: vv-fade-out ${FADE_MS}ms ease-in both; }
@keyframes vv-fade-in { from { opacity: 0 } to { opacity: 1 } }
@keyframes vv-fade-out { from { opacity: 1 } to { opacity: 0 } }
.vv-kenburns { animation: vv-kenburns 9s ease-out both; transform-origin: 50% 45%; will-change: transform; }
@keyframes vv-kenburns { from { transform: scale(1.02) translate3d(0, 0, 0) } to { transform: scale(1.14) translate3d(0, -18px, 0) } }
.vv-rise { animation: vv-rise 900ms cubic-bezier(.2,.7,.2,1) both; }
@keyframes vv-rise { from { opacity: 0; transform: translate3d(0, 46px, 0) } to { opacity: 1; transform: none } }
.vv-reveal { animation: vv-reveal 1200ms cubic-bezier(.2,.7,.2,1) both; }
@keyframes vv-reveal { from { opacity: 0; clip-path: inset(0 0 100% 0); transform: translate3d(0, 30px, 0) } to { opacity: 1; clip-path: inset(0 0 0 0); transform: none } }
.vv-pop { animation: vv-pop 900ms cubic-bezier(.2,.9,.3,1.2) both; }
@keyframes vv-pop { from { opacity: 0; transform: scale(.88) } to { opacity: 1; transform: none } }
.vv-slide { animation: vv-slide 800ms cubic-bezier(.2,.7,.2,1) both; }
@keyframes vv-slide { from { opacity: 0; transform: translate3d(-120px, 0, 0) } to { opacity: 1; transform: none } }
.vv-flip { animation: vv-flip 900ms cubic-bezier(.2,.7,.2,1) both; transform-origin: 50% 0; }
@keyframes vv-flip { from { opacity: 0; transform: perspective(1200px) rotateX(-35deg) } to { opacity: 1; transform: none } }
.vv-sweep { animation: vv-sweep 3.2s ease-in-out 700ms both; }
@keyframes vv-sweep { from { transform: translate3d(-110%, 0, 0); opacity: 0 } 20% { opacity: 1 } 80% { opacity: 1 } to { transform: translate3d(110%, 0, 0); opacity: 0 } }
.vv-sweep-down { animation: vv-sweep-down 4s ease-in-out 1.2s infinite; top: 0; }
@keyframes vv-sweep-down { from { transform: translate3d(0, -120px, 0); opacity: 0 } 15% { opacity: 1 } 85% { opacity: 1 } to { transform: translate3d(0, 900px, 0); opacity: 0 } }
.vv-float { animation: vv-float 9s ease-in-out both; }
@keyframes vv-float { from { transform: translate3d(0, 60px, 0) scale(1.05) } to { transform: translate3d(0, -80px, 0) scale(1.1) } }
.vv-sway { animation: vv-sway 6s ease-in-out infinite alternate; transform-origin: 50% 0; }
@keyframes vv-sway { from { transform: rotate(-2.5deg) translate3d(0, 0, 0) } to { transform: rotate(2.5deg) translate3d(0, 20px, 0) } }
.vv-shadow { text-shadow: 0 2px 18px rgba(0,0,0,.9), 0 0 4px rgba(0,0,0,.6); }
.vv-glow { text-shadow: 0 0 40px rgba(197, 26, 106, .9), 0 0 90px rgba(197, 26, 106, .5); }
.vv-pulse { animation: vv-pop 900ms cubic-bezier(.2,.9,.3,1.2) both, vv-pulse 2.4s ease-in-out 1.5s infinite; }
@keyframes vv-pulse { 50% { transform: scale(1.05); text-shadow: 0 0 50px rgba(197, 26, 106, .9) } }
.vv-gradient { background: radial-gradient(60% 40% at 20% 20%, rgba(197,26,106,.55), transparent 70%), radial-gradient(55% 35% at 85% 80%, rgba(197,26,106,.45), transparent 70%), radial-gradient(40% 30% at 70% 30%, rgba(200,200,215,.18), transparent 70%), #050304; background-size: 160% 160%; animation: vv-gradient 14s ease-in-out infinite alternate; }
@keyframes vv-gradient { from { background-position: 0% 0% } to { background-position: 100% 100% } }
`;
